import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { TURNSTILE_SITE_KEY, loadTurnstile } from '../utils/turnstile';

export interface TurnstileHandle {
  /** Clears the current token and asks Turnstile for a fresh challenge. */
  reset: () => void;
}

interface TurnstileWidgetProps {
  /** Called with a token when the challenge resolves, and with '' when it expires, errors, or is reset. */
  onToken: (token: string) => void;
  /** Optional label sent to Turnstile so widgets can be told apart in analytics. */
  action?: string;
  className?: string;
}

/**
 * Explicitly-rendered Turnstile widget. Explicit render (rather than the
 * implicit `cf-turnstile` class scan) is what lets us reset the widget after
 * every submission, since Turnstile tokens are single-use.
 */
const TurnstileWidget = forwardRef<TurnstileHandle, TurnstileWidgetProps>(
  ({ onToken, action, className = '' }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const onTokenRef = useRef(onToken);
    const [loadFailed, setLoadFailed] = useState(false);

    // Keep the latest callback without re-rendering the widget.
    useEffect(() => {
      onTokenRef.current = onToken;
    }, [onToken]);

    useEffect(() => {
      let cancelled = false;

      loadTurnstile()
        .then((turnstile) => {
          if (cancelled || !containerRef.current) return;
          widgetIdRef.current =
            turnstile.render(containerRef.current, {
              sitekey: TURNSTILE_SITE_KEY,
              action,
              callback: (token: string) => onTokenRef.current(token),
              'expired-callback': () => onTokenRef.current(''),
              'timeout-callback': () => onTokenRef.current(''),
              'error-callback': () => onTokenRef.current(''),
            }) ?? null;
        })
        .catch(() => {
          if (!cancelled) setLoadFailed(true);
        });

      return () => {
        cancelled = true;
        const widgetId = widgetIdRef.current;
        if (widgetId && window.turnstile) {
          try {
            window.turnstile.remove(widgetId);
          } catch {
            // Widget was already torn down with its container.
          }
        }
        widgetIdRef.current = null;
      };
    }, [action]);

    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          const widgetId = widgetIdRef.current;
          if (widgetId && window.turnstile) {
            try {
              window.turnstile.reset(widgetId);
            } catch {
              // Nothing to reset; the token is cleared below either way.
            }
          }
          onTokenRef.current('');
        },
      }),
      []
    );

    return (
      <div className={className}>
        <div ref={containerRef} />
        {loadFailed && (
          <p className="text-sm text-red-600 mt-2">
            Could not load the verification challenge. Please refresh the page or
            call us at (509) 901-7476.
          </p>
        )}
      </div>
    );
  }
);

TurnstileWidget.displayName = 'TurnstileWidget';

export default TurnstileWidget;
